import { jsx as _jsx } from "react/jsx-runtime";
import noop from 'lodash/noop';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useWizard } from 'react-use-wizard';
import { useDrawerContext } from './DrawerContext';
////////////////////
// CONTEXT
////////////////////
const MultiStepDrawerContext = createContext(undefined);
/**
 * Custom hook to access the drawer context.
 * This hook provides access to the drawer state and actions within the `DrawerContextProvider`.
 * It throws an error if used outside of a `DrawerContextProvider`.
 * @returns {DrawerState} The current drawer state and actions.
 */
export const useMultiStepDrawer = () => {
    const wizardContext = useWizard();
    const drawerContext = useDrawerContext();
    const multiStepContext = useContext(MultiStepDrawerContext);
    if (!multiStepContext) {
        throw new Error('useMultiStepDrawer must be used within a MultiStepDrawerContextProvider');
    }
    const context = useMemo(() => ({ ...drawerContext, ...multiStepContext }), [drawerContext, multiStepContext]);
    // Wizard Wrappers
    const nextStep = useCallback(async () => {
        try {
            await wizardContext.nextStep();
        }
        catch (e) {
            context.nextStepErrorHandler.current?.(e);
        }
    }, [wizardContext, context]);
    const isLoading = context.isLoading || wizardContext.isLoading;
    return { ...context, ...wizardContext, nextStep, isLoading };
};
////////////////////
// PROVIDER
////////////////////
export const MultiStepDrawerContextProvider = (props) => {
    const { children } = props;
    const nextStepErrorHandler = useRef(noop);
    const handleStepError = useCallback((handler) => {
        nextStepErrorHandler.current = handler;
    }, []);
    const multiStepContextValue = useMemo(() => ({
        handleStepError,
        nextStepErrorHandler,
    }), [handleStepError, nextStepErrorHandler]);
    return _jsx(MultiStepDrawerContext.Provider, { value: multiStepContextValue, children: children });
};
